<template>
  <div>
    <v-card outlined rounded="lg" class="pa-4 mb-3">
      <div class="d-flex align-center justify-space-between mb-3">
        <div class="orbitron-title">Mapa de activos</div>

        <v-btn small outlined @click="recargar" :loading="cargando">
          Recargar
        </v-btn>
      </div>
    </v-card>

    <v-card outlined rounded="lg" class="pa-4">
      <v-alert v-if="error" type="error" dense class="mb-3">
        {{ error }}
      </v-alert>

      <div v-if="!cargando && totalActivos === 0" class="text-caption grey--text mb-3">
        No hay activos con coordenadas para mostrar.
      </div>
      <v-text-field
        v-model="busqueda"
        dense
        outlined
        clearable
        prepend-inner-icon="mdi-magnify"
        label="Buscar activo"
        class="mb-3 buscador-activos"
        @input="buscarConDebounce"
        @click:clear="limpiarBusqueda"
      />
      <div class="panel-mapa">
        <div id="mapa-activos" class="mapa"></div>

        <v-card class="leyenda mb-3 pa-2" @click.stop @mousedown.stop @touchstart.stop>
          <div class="text-subtitle-2 mb-3">Tecnologías</div>

          <div class="d-flex flex-wrap ga-2">
            <v-chip
              small
              color="black"
              outlined
              class="legend-chip"
              :class="{ 'chip-activo': filtroTecnologia === null && !filtroEnMercado && !filtroMiPlantilla }"
              @click.stop="limpiarFiltros"
            >
              Todos
            </v-chip>

            <v-chip
              small
              outlined
              class="legend-chip"
              :class="{ 'chip-activo': filtroTecnologia === 'solar' }"
              @click.stop="toggleFiltroTecnologia('solar')"
            >
              <span class="dot mr-2" style="background:#FDB813;"></span> Solar
            </v-chip>

            <v-chip
              small
              outlined
              class="legend-chip"
              :class="{ 'chip-activo': filtroTecnologia === 'eolica' }"
              @click.stop="toggleFiltroTecnologia('eolica')"
            >
              <span class="dot mr-2" style="background:#248F11;"></span> Eólica
            </v-chip>

            <v-chip
              small
              outlined
              class="legend-chip"
              :class="{ 'chip-activo': filtroTecnologia === 'hidraulica' }"
              @click.stop="toggleFiltroTecnologia('hidraulica')"
            >
              <span class="dot mr-2" style="background:#4AA3DF;"></span> Hidráulica
            </v-chip>

            <v-chip
              small
              outlined
              class="legend-chip"
              :class="{ 'chip-activo': filtroTecnologia === 'nuclear' }"
              @click.stop="toggleFiltroTecnologia('nuclear')"
            >
              <span class="dot mr-2" style="background:#8E44AD;"></span> Nuclear
            </v-chip>

            <v-chip
              small
              outlined
              class="legend-chip"
              :class="{ 'chip-activo': filtroTecnologia === 'gas' }"
              @click.stop="toggleFiltroTecnologia('gas')"
            >
              <span class="dot mr-2" style="background:#E67E22;"></span> Gas / Ciclo
            </v-chip>

            <v-chip
              small
              outlined
              class="legend-chip"
              :class="{ 'chip-activo': filtroEnMercado }"
              @click.stop="toggleFiltroMercado"
            >
              <span class="plantilla-mini-badge mr-2">$</span> En mercado
            </v-chip>
            <v-chip
              small
              outlined
              class="legend-chip"
              :class="{ 'chip-activo': filtroMiPlantilla }"
              @click.stop="toggleFiltroMiPlantilla"
            >
              <span class="dot market-dot mr-2"></span> Mi plantilla
            </v-chip>
          </div>
        </v-card>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from "@/axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export default {
  name: "AssetsMap",
  data() {
    return {
      cargando: false,
      error: "",
      geojson: { type: "FeatureCollection", features: [] },
      layerActivos: null,
      resizeTimer: null,
      debounceTimer: null, // Timer para optimizar el buscador
      filtroTecnologia: null,
      filtroEnMercado: false,
      filtroMiPlantilla: false,
      busqueda: "",
      provinciasData: {},
      minWind: 0,
      maxWind: 0,
      minIrrad: 0,
      maxIrrad: 0,
      minCo2: 0,
      maxCo2: 0,
      co2Unit: "ppm",
      irradLayer: null,
      windLayer: null,
      co2Layer: null,
      leyendasMeteo: {},
    };
  },
  computed: {
    totalActivos() {
      return this.geojson?.features?.length || 0;
    },
  },
  created() {
    // Fuera de data() a propósito: la plantilla nunca lee el mapa (lo pinta Leaflet
    // sobre #mapa-activos), así que no debe pasar por el sistema de reactividad.
    // Observarlo obligaría a Vue a recorrer panes, capas y nodos DOM con
    // Object.defineProperty. Misma convención que this._layerControl.
    this._leafletMap = null;
  },
  mounted() {
    this.$nextTick(async () => {
      this._ensureMap();
      await this.recargar();
      this._scheduleResize();
    });
  },
  beforeDestroy() {
    if (this._leafletMap) {
      this._leafletMap.remove();
      this._leafletMap = null;
    }
    clearTimeout(this.debounceTimer);
    clearTimeout(this.resizeTimer);
  },
  methods: {
    normalizarTexto(v) {
      return (v || "")
        .toString()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();
    },

    buscarConDebounce() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this._drawActivos();
      }, 300); // 300ms de retardo antes de redibujar
    },

    limpiarBusqueda() {
      this.busqueda = "";
      this._drawActivos();
    },

    money(v) {
      if (v == null || isNaN(v)) return "—";
      return new Intl.NumberFormat("es-ES", {
        maximumFractionDigits: 0,
      }).format(v);
    },

    normalizarTecnologia(tecnologia) {
      const t = (tecnologia || "").toString().trim().toLowerCase();

      if (t.includes("solar") || t.includes("fotovolta")) return "solar";
      if (t.includes("eólica") || t.includes("eolica")) return "eolica";
      if (t.includes("hidro")) return "hidraulica";
      if (t.includes("nuclear")) return "nuclear";
      if (t.includes("ciclo") || t.includes("gas")) return "gas";

      return "otros";
    },

    toggleFiltroTecnologia(tecnologia) {
      this.filtroTecnologia =
        this.filtroTecnologia === tecnologia ? null : tecnologia;
      this._drawActivos();
    },

    toggleFiltroMercado() {
      this.filtroEnMercado = !this.filtroEnMercado;
      this._drawActivos();
    },

    toggleFiltroMiPlantilla() {
      this.filtroMiPlantilla = !this.filtroMiPlantilla;
      this._drawActivos();
    },

    limpiarFiltros() {
      this.filtroTecnologia = null;
      this.filtroEnMercado = false;
      this.filtroMiPlantilla = false;
      this.busqueda = "";
      this._drawActivos();
    },

    pasaFiltrosActivo(p) {
      const tecnologia = this.normalizarTecnologia(p.tecnologia);

      if (this.filtroTecnologia && tecnologia !== this.filtroTecnologia) {
        return false;
      }

      if (this.filtroEnMercado && !p.en_mercado) {
        return false;
      }

      if (this.filtroMiPlantilla && !p.en_plantilla) {
        return false;
      }

      const q = this.normalizarTexto(this.busqueda);

      if (q) {
        const textoActivo = [
          p.nombre,
          p.tecnologia,
          p.municipio,
          p.provincia,
          p.fase1,
          p.mw,
        ]
          .map(this.normalizarTexto)
          .join(" ");

        if (!textoActivo.includes(q)) {
          return false;
        }
      }

      return true;
    },

    _getEl(id) {
      return this.$el?.querySelector?.(`#${id}`) || null;
    },

    _safeInitMap(elId, initFn) {
      const el = this._getEl(elId);
      if (!el) return false;
      if (el._leaflet_id) return true;
      initFn(el);
      return true;
    },

    _scheduleResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        requestAnimationFrame(() => {
          try {
            if (this._leafletMap) this._leafletMap.invalidateSize();
          } catch (e) {
            console.error("[AssetsMap] resize error:", e);
          }
        });
      }, 120);
    },

    _ensureMap() {
      if (this._leafletMap) return true;

      return this._safeInitMap("mapa-activos", (el) => {
        this._leafletMap = L.map(el, {
          zoomControl: true,
          zoomAnimation: false,
          fadeAnimation: false,
        }).setView([40.3, -3.7], 6);

        const cartoApiKey = process.env.VUE_APP_CARTO_API_KEY;

        L.tileLayer(
          `https://{s}.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}.png?key=${cartoApiKey}`,
          {
            subdomains: "abcd",
            maxZoom: 20,
            attribution: "© OpenStreetMap, © CARTO",
          }
        ).addTo(this._leafletMap);

        this._addCapasLeyendas();

        // Lanzamos una sola carga unificada para las capas de provincias
        this._cargarCapasProvincias().catch((e) =>
          console.error("[AssetsMap] Error cargando capas de provincias:", e)
        );

        this._drawActivos();
      });
    },

    _mostrarPopupProvincia(nombre, latlng) {
      if (!this._leafletMap) return;

      const info = this.provinciasData[nombre] || {};
      const txtIrrad = info.irrad != null ? `${info.irrad.toFixed(0)} W/m²` : "sin datos";
      const txtWind = info.wind != null ? `${info.wind.toFixed(2)} km/h` : "sin datos";
      const txtCO2= info.co2 != null ? `${info.co2.toFixed(2)} ${this.co2Unit}` : "sin datos";

      let html = `
        <div style="min-width: 160px; font-family: sans-serif;">
          <div style="font-weight: bold; border-bottom: 1px solid #ccc; padding-bottom: 4px; margin-bottom: 6px; font-size: 13px;">
            ${nombre}
          </div>
          <div style="margin-bottom: 4px;">☀️ <b>Irradiancia:</b> ${txtIrrad}</div>
          <div>🌬️ <b>Viento (100 m):</b> ${txtWind}</div>
          <div>💨 <b>Co2:</b> ${txtCO2}</div>
        </div>
      `;

      L.popup({ autoPan: true })
        .setLatLng(latlng)
        .setContent(html)
        .openOn(this._leafletMap);
    },

    _addCapasLeyendas() {
 
      this._leafletMap.on("overlayadd", (e) => {
        const leg = this.leyendasMeteo?.[e.name];
        if (leg) leg.addTo(this._leafletMap);
      });
      this._leafletMap.on("overlayremove", (e) => {
        const leg = this.leyendasMeteo?.[e.name];
        if (leg) leg.remove();
      });
    },

    _crearLeyendaIrrad() {
      const pasos = 6;
      const escala = [];

      for (let i = 0; i < pasos; i++) {
        // Color muestreado en el CENTRO del bucket para que coincida con lo que
        // el mapa pinta en ese rango (misma normalización que _colorIrrad).
        const p = (i + 0.5) / pasos;
        const lightness = 90 - p * 45; // 90% pálido (poco sol) → 45% oro intenso (mucho sol)

        const desde =
          this.minIrrad +
          (this.maxIrrad - this.minIrrad) * (i / pasos);

        const hasta =
          this.minIrrad +
          (this.maxIrrad - this.minIrrad) * ((i + 1) / pasos);

        escala.push({
          c: `hsl(45,95%,${lightness}%)`,
          t: `${desde.toFixed(0)} - ${hasta.toFixed(0)} W/m²`,
        });
      }

      return this._crearLeyendaEscalones(
        "Irradiancia solar (W/m²)",
        escala,
        this._rangoGradiente(this.minIrrad, this.maxIrrad, this._colorIrrad, 0)
      );
    },

    _colorWind(wind) {
      if (wind == null || isNaN(wind)) return "#cccccc";

      if (this.maxWind === this.minWind) {
        return "hsl(210, 90%, 60%)";
      }

      const p = (wind - this.minWind) / (this.maxWind - this.minWind);
      const lightness = 92 - p * 52;

      return `hsl(210, 90%, ${lightness}%)`;
    },

    _crearLeyendaWind() {
      const pasos = 6;
      const escala = [];

      for (let i = 0; i < pasos; i++) {
        // Color muestreado en el CENTRO del bucket para que coincida con lo que
        // el mapa pinta en ese rango (misma normalización que _colorWind).
        const p = (i + 0.5) / pasos;
        const lightness = 92 - p * 52;

        const desde =
          this.minWind +
          (this.maxWind - this.minWind) * (i / pasos);

        const hasta =
          this.minWind +
          (this.maxWind - this.minWind) * ((i + 1) / pasos);

        escala.push({
          c: `hsl(210,90%,${lightness}%)`,
          t: `${desde.toFixed(1)} - ${hasta.toFixed(1)} km/h`,
        });
      }

      return this._crearLeyendaEscalones(
        "Velocidad media (km/h)",
        escala,
        this._rangoGradiente(this.minWind, this.maxWind, this._colorWind, 1)
      );
    },

    _colorIrrad(v) {
      if (v == null || isNaN(v)) return "#cccccc";

      if (this.maxIrrad === this.minIrrad) {
        return "hsl(45, 95%, 60%)";
      }

      const p = (v - this.minIrrad) / (this.maxIrrad - this.minIrrad);
      const lightness = 90 - p * 45; // 90% pálido (poco sol) → 45% oro intenso (mucho sol)

      return `hsl(45, 95%, ${lightness}%)`;
    },

    _colorCo2(v) {
      if (v == null || isNaN(v)) return "#cccccc";

      if (this.maxCo2 === this.minCo2) {
        return "hsl(0, 0%, 55%)";
      }

      const p = (v - this.minCo2) / (this.maxCo2 - this.minCo2);
      const lightness = 85 - p * 60; // 85% gris claro (poco CO2) → 25% carbón (mucho CO2)

      return `hsl(0, 0%, ${lightness}%)`;
    },

    _crearLeyendaCo2() {
      const pasos = 6;
      const escala = [];

      for (let i = 0; i < pasos; i++) {
        // Color en el CENTRO del bucket (misma normalización que _colorCo2)
        const p = (i + 0.5) / pasos;
        const lightness = 85 - p * 60;

        const desde =
          this.minCo2 +
          (this.maxCo2 - this.minCo2) * (i / pasos);

        const hasta =
          this.minCo2 +
          (this.maxCo2 - this.minCo2) * ((i + 1) / pasos);

        escala.push({
          c: `hsl(0,0%,${lightness}%)`,
          t: `${desde.toFixed(1)} - ${hasta.toFixed(1)} ${this.co2Unit}`,
        });
      }

      return this._crearLeyendaEscalones(
        `CO₂ (${this.co2Unit})`,
        escala,
        this._rangoGradiente(this.minCo2, this.maxCo2, this._colorCo2, 1)
      );
    },

    /**
     * UNIFICADO: Carga el GeoJSON de provincias una sola vez y consulta Open-Meteo para
     * obtener irradiancia solar y viento a 100 m (API forecast) y CO2 (API air-quality) de
     * todas las provincias, construyendo con ello las tres capas choropleth y sus leyendas.
     */
    async _cargarCapasProvincias() {
      if (!this._leafletMap) return;

      if (!this._layerControl) {
        this._layerControl = L.control
          .layers(null, null, { collapsed: false, position: "topright" })
          .addTo(this._leafletMap);
      }

      // 1. Un solo Fetch del GeoJSON de provincias
      // const url = `${process.env.BASE_URL}geo/spain-provinces.json`;
      // const geo = await fetch(url).then((r) => {
      //   if (!r.ok) throw new Error(`GeoJSON HTTP ${r.status}`);
      //   return r.json();
      // });
      const url = "/api/mapa/provincias/";

      let geo = null;
      try {
        const res = await axios.get(url);
        // El backend responde { error, geojson: <FeatureCollection> }
        geo = res.data?.geojson || null;
      } catch (e) {
        console.error("[AssetsMap] _cargarCapasProvincias ERROR:", e);
        geo = null;
      }

      if (!geo || !Array.isArray(geo.features)) {
        console.warn("[AssetsMap] GeoJSON de provincias vacío o inválido");
        return;
      }

      const feats = geo.features;

      // 2. Coordenadas para Open-Meteo tomadas de los qlat/qlon del propio GeoJSON,
      //    así el orden coincide siempre con el de los features devueltos por la API.
      const lats = feats.map((f) => f.properties.qlat);
      const lons = feats.map((f) => f.properties.qlon);

      // Unica llamada a API de Open-Meteo: irradiancia solar (GHI) y viento a altura de buje (100 m)
      const omUrl =
        `https://api.open-meteo.com/v1/forecast?latitude=${lats}` +
        `&longitude=${lons}&current=shortwave_radiation,wind_speed_100m&timezone=auto`;

      // CO2 desde la API de calidad del aire de Open-Meteo (mismas coords, mismo orden)
      const co2Url =
        `https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${lats}` +
        `&longitude=${lons}&current=carbon_dioxide&timezone=auto`;

      const datos = await fetch(omUrl).then((r) => {
        if (!r.ok) throw new Error(`Open-Meteo HTTP ${r.status}`);
        return r.json();
      });
      const arr = Array.isArray(datos) ? datos : [datos];

      // CO2 en su propia llamada con try/catch: si falla, no rompe irradiancia/viento
      let arrCo2 = [];
      try {
        const datosCo2 = await fetch(co2Url).then((r) => {
          if (!r.ok) throw new Error(`Open-Meteo Air-Quality HTTP ${r.status}`);
          return r.json();
        });
        arrCo2 = Array.isArray(datosCo2) ? datosCo2 : [datosCo2];
        this.co2Unit = arrCo2[0]?.current_units?.carbon_dioxide || this.co2Unit;
      } catch (e) {
        console.error("[AssetsMap] Open-Meteo Air-Quality (CO2) ERROR:", e);
        arrCo2 = [];
      }

      // 3. Procesamos los datos una sola vez
      feats.forEach((f, i) => {
        const currentData = arr[i]?.current;
        const irrad = currentData?.shortwave_radiation ?? null;
        const wind = currentData?.wind_speed_100m ?? null;
        const co2 = arrCo2[i]?.current?.carbon_dioxide ?? null;

        f.properties.irrad = irrad;
        f.properties.wind = wind;
        f.properties.co2 = co2;

        const nombreProvincia = f.properties.name;
        if (!this.provinciasData[nombreProvincia]) {
          this.$set(this.provinciasData, nombreProvincia, {});
        }
        this.provinciasData[nombreProvincia].irrad = irrad;
        this.provinciasData[nombreProvincia].wind = wind;
        this.provinciasData[nombreProvincia].co2 = co2;
      });


      // 4. Calculamos límites para las escalas visuales
      const valoresIrrad = feats
        .map((f) => f.properties.irrad)
        .filter((v) => v != null && !isNaN(v));
      this.minIrrad = valoresIrrad.length ? Math.min(...valoresIrrad) : 0;
      this.maxIrrad = valoresIrrad.length ? Math.max(...valoresIrrad) : 0;

      const valoresWind = feats
        .map((f) => f.properties.wind)
        .filter((v) => v != null && !isNaN(v));
      this.minWind = valoresWind.length ? Math.min(...valoresWind) : 0;
      this.maxWind = valoresWind.length ? Math.max(...valoresWind) : 0;

      const valoresCo2 = feats
        .map((f) => f.properties.co2)
        .filter((v) => v != null && !isNaN(v));
      this.minCo2 = valoresCo2.length ? Math.min(...valoresCo2) : 0;
      this.maxCo2 = valoresCo2.length ? Math.max(...valoresCo2) : 0;

      // 5. Crear el panel si no existe
      if (!this._leafletMap.getPane("provinciasPane")) {
        this._leafletMap.createPane("provinciasPane");
        this._leafletMap.getPane("provinciasPane").style.zIndex = 350;
      }
      const renderer = L.svg({ pane: "provinciasPane" });

      // 6. Construimos la Capa de Irradiancia solar
      const layerIrrad = L.geoJSON(geo, {
        pane: "provinciasPane",
        renderer,
        style: (f) => ({
          color: "#555",
          weight: 1,
          fillColor: this._colorIrrad(f.properties.irrad),
          fillOpacity: 0.65,
        }),
        onEachFeature: (f, lyr) => {
          lyr.bindTooltip(`<b>${f.properties.name}</b>`, {
            sticky: true,
          });

          lyr.on("click", (e) => {
            L.DomEvent.stopPropagation(e);
            this._mostrarPopupProvincia(f.properties.name, e.latlng);
          });
        },
      });

      // 7. Construimos la Capa de Viento (reutiliza el mismo set de datos de memoria)
      const layerWind = L.geoJSON(geo, {
        pane: "provinciasPane",
        renderer,
        style: (f) => ({
          color: "#555",
          weight: 1,
          fillColor: this._colorWind(f.properties.wind),
          fillOpacity: 0.65,
        }),
        onEachFeature: (f, lyr) => {
          //const w = f.properties.wind;
          //const txt = w != null ? `${w.toFixed(2)}` : "sin dato";
          lyr.bindTooltip(`<b>${f.properties.name}</b>`, {
            sticky: true,
          });

          lyr.on("click", (e) => {
            L.DomEvent.stopPropagation(e);
            this._mostrarPopupProvincia(f.properties.name, e.latlng);
          });
        },
      });

      // 8. Construimos la Capa de CO2 (reutiliza el mismo set de datos de memoria)
      const layerCo2 = L.geoJSON(geo, {
        pane: "provinciasPane",
        renderer,
        style: (f) => ({
          color: "#555",
          weight: 1,
          fillColor: this._colorCo2(f.properties.co2),
          fillOpacity: 0.65,
        }),
        onEachFeature: (f, lyr) => {
          lyr.bindTooltip(`<b>${f.properties.name}</b>`, {
            sticky: true,
          });

          lyr.on("click", (e) => {
            L.DomEvent.stopPropagation(e);
            this._mostrarPopupProvincia(f.properties.name, e.latlng);
          });
        },
      });

      // Asignar y registrar las capas en el controlador del mapa
      this.irradLayer = layerIrrad;
      this.windLayer = layerWind;
      this.co2Layer = layerCo2;

      this._layerControl.addOverlay(layerIrrad, "Irradiancia solar");
      this._layerControl.addOverlay(layerWind, "Viento provincias");
      this._layerControl.addOverlay(layerCo2, "CO2 provincias");

      // Añadimos sus leyendas correspondientes
      this.leyendasMeteo = this.leyendasMeteo || {};
      this.leyendasMeteo["Irradiancia solar"] = this._crearLeyendaIrrad();
      this.leyendasMeteo["Viento provincias"] = this._crearLeyendaWind();
      this.leyendasMeteo["CO2 provincias"] = this._crearLeyendaCo2();
    },

    /**
     * Versión compacta de la escala para móvil: una sola barra de degradado con
     * los extremos etiquetados. Los colores se muestrean llamando a la misma
     * función que pinta el mapa, así el degradado no puede desincronizarse.
     */
    _rangoGradiente(min, max, colorFn, decimales) {
      const colores = [0, 0.25, 0.5, 0.75, 1].map((p) =>
        colorFn.call(this, min + (max - min) * p)
      );

      return {
        colores,
        min: Number(min).toFixed(decimales),
        max: Number(max).toFixed(decimales),
      };
    },

    _crearLeyendaEscalones(titulo, stops, rango) {
      const control = L.control({ position: "bottomleft" });
      control.onAdd = () => {
        const div = L.DomUtil.create("div", "meteo-leyenda");
        const filas = stops
          .map(
            (s) =>
              `<div class="meteo-swatch-fila">
                 <span class="meteo-swatch" style="background:${s.c};"></span>
                 <span>${s.t}</span>
               </div>`
          )
          .join("");

        // Se emiten las dos versiones y es el CSS (@media) quien elige cuál se
        // ve. Así un giro de pantalla no obliga a reconstruir el control.
        const gradiente = rango
          ? `<div class="meteo-gradiente">
               <div class="meteo-barra" style="background:linear-gradient(to right, ${rango.colores.join(
                 ", "
               )});"></div>
               <div class="meteo-barra-extremos">
                 <span>${rango.min}</span>
                 <span>${rango.max}</span>
               </div>
             </div>`
          : "";

        div.innerHTML = `
          <div class="meteo-leyenda-titulo">${titulo}</div>
          <div class="meteo-swatches">${filas}</div>
          ${gradiente}`;
        return div;
      };
      return control;
    },

    async recargar() {
      try {
        this.cargando = true;
        this.error = "";

        const res = await axios.get("/api/mapa/activos");
        this.geojson = res?.data?.data || { type: "FeatureCollection", features: [] };

        if (this._leafletMap) {
          this._drawActivos();
          this._leafletMap.invalidateSize();
        }
      } catch (e) {
        this.error = e?.response?.data?.message || "No se pudo cargar el mapa de activos.";
      } finally {
        this.cargando = false;
      }
    },

    _drawActivos() {
      if (!this._leafletMap || !this.geojson) return;

      if (this.layerActivos) {
        this.layerActivos.removeFrom(this._leafletMap);
        this.layerActivos = null;
      }

      const usados = {};
      const group = L.layerGroup();

      (this.geojson.features || []).forEach((feature) => {
        const coords = feature?.geometry?.coordinates || [];
        const p = feature?.properties || {};
        if (!this.pasaFiltrosActivo(p)) return;

        if (coords.length < 2) return;

        const lng = Number(coords[0]);
        const lat = Number(coords[1]);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

        const key = `${lng}|${lat}`;
        const index = usados[key] || 0;
        usados[key] = index + 1;

        let finalLat = lat;
        let finalLng = lng;

        if (index > 0) {
          const step = 0.01;
          const angle = (index - 1) * (Math.PI / 4);
          finalLat = lat + Math.sin(angle) * step;
          finalLng = lng + Math.cos(angle) * step;
        }

        const color = p.color || "#757575";
        const enMercado = Boolean(p.en_mercado);
        const enPlantilla = Boolean(p.en_plantilla);
        const plantillaOperativo = Boolean(p.plantilla_operativo);

        if (enPlantilla) {
          L.circleMarker([finalLat, finalLng], {
            radius: 17,
            color,
            weight: 2,
            opacity: 0.95,
            fillColor: color,
            fillOpacity: 0.18,
            className: "activo-market-glow",
            interactive: false,
          }).addTo(group);
        }

        const marker = L.circleMarker([finalLat, finalLng], {
          radius: enPlantilla ? 9 : 7,
          color: enPlantilla ? "#ffffff" : "#1a1a1a",
          weight: enPlantilla ? 2 : 0.8,
          fillColor: color,
          fillOpacity: enPlantilla ? 1 : 0.9,
          className: enPlantilla ? "activo-market-core" : "",
        });

        const precioMercado =
          p.precio_mercado != null ? `${this.money(p.precio_mercado)} €` : "—";

        const estadoPlantilla = enPlantilla
          ? plantillaOperativo
            ? "En tu plantilla · Activado"
            : "En tu plantilla · No activado"
          : "";

        const popup = `
          <div style="min-width: 200px;">
            <b>${p.nombre || "Activo"}</b><br/>
            <b>Tecnología:</b> ${p.tecnologia || "—"}<br/>
            <b>MW:</b> ${p.mw ?? "—"}<br/>
            <b>Municipio:</b> ${p.municipio || "—"}<br/>
            <b>Provincia:</b> ${p.provincia || "—"}<br/>
            <b>Propietario:</b> ${p.propietario_nombre || "Mercado"}<br/>
            
            ${
              enPlantilla
                ? `<br/><br/>
                  <span style="
                    display:inline-block;
                    padding:4px 8px;
                    border-radius:999px;
                    background:${plantillaOperativo ? "#e8f5e9" : "#fff8e1"};
                    color:${plantillaOperativo ? "#2e7d32" : "#8a6d00"};
                    font-weight:700;
                  ">
                    ${estadoPlantilla}
                  </span>`
                : ""
            }

            ${
              enMercado
                ? `<br/><br/>
                  <span style="
                    display:inline-block;
                    padding:4px 8px;
                    border-radius:999px;
                    background:#e8f5e9;
                    color:#2e7d32;
                    font-weight:700;
                  ">
                    Disponible en mercado
                  </span>
                  <br/>
                  <b>Precio mínimo:</b> ${precioMercado}`
                : ""
            }
          </div>
        `;

        marker.bindPopup(popup);
        marker.addTo(group);

        if (enMercado) {
          const marketIcon = L.divIcon({
            className: "activo-owned-icon",
            html: `<div class="activo-owned-badge operativo">$</div>`,
            iconSize: [22, 22],
            iconAnchor: [11, 26],
          });

          L.marker([finalLat, finalLng], {
            icon: marketIcon,
            interactive: false,
            keyboard: false,
          }).addTo(group);
        }
      });

      this.layerActivos = group;
      this.layerActivos.addTo(this._leafletMap);
    },
  },
};
</script>

<style scoped>
.panel-mapa {
  position: relative;
  width: 100%;
  height: 500px;
}

.mapa {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  z-index: 1;
}

.leyenda {
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 1000;
  max-width: 320px;
  background-color: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
}

/* En móvil la leyenda de tecnologías ocupa casi todo el ancho y tapa las
   leyendas meteo de Leaflet (bottomleft), que además se apilan una encima de la otra
   La sacamos del overlay y la colocamos debajo del mapa. */
@media (max-width: 600px) {
  .panel-mapa {
    height: auto;
  }

  .mapa {
    height: 340px;
  }

  .leyenda {
    position: static;
    max-width: none;
    margin-top: 8px;
    backdrop-filter: none;
  }
}

.legend-chip {
  cursor: pointer;
  transition: all 0.2s ease;
}

.legend-chip:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.chip-activo {
  background: #31635f !important;
  color: black !important;
  border-color: #31635f !important;
  box-shadow: 0 3px 10px rgba(49, 99, 95, 0.25);

}

.chip-activo .dot {
  border: 1px solid white;
}

.dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.2);
}

.market-dot {
  background: #2e7d32;
  box-shadow: 0 0 4px #2e7d32;
}

.plantilla-mini-badge {
  background: #1565c0;
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
}

.orbitron-title {
  font-family: 'Orbitron', sans-serif;
  font-weight: bold;
  font-size: 1.1rem;
}

/* Clases inyectadas de forma dinámica en Leaflet */
::v-deep .meteo-leyenda {
  background: white;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.2);
  font-family: sans-serif;
  font-size: 11px;
}

::v-deep .meteo-leyenda-titulo {
  font-weight: bold;
  margin-bottom: 6px;
  border-bottom: 1px solid #eee;
  padding-bottom: 3px;
}

::v-deep .meteo-swatches {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

::v-deep .meteo-swatch-fila {
  display: flex;
  align-items: center;
  gap: 8px;
}

::v-deep .meteo-swatch {
  display: inline-block;
  width: 18px;
  height: 12px;
  border-radius: 2px;
  border: 1px solid rgba(0, 0, 0, 0.15);
}

/* Barra de degradado: oculta en escritorio, sustituye a los 6 escalones en
   móvil para que las tres leyendas quepan a la vez sobre el mapa. */
::v-deep .meteo-gradiente {
  display: none;
}

::v-deep .meteo-barra {
  height: 10px;
  width: 120px;
  border-radius: 2px;
  border: 1px solid rgba(0, 0, 0, 0.15);
}

::v-deep .meteo-barra-extremos {
  display: flex;
  justify-content: space-between;
  margin-top: 2px;
}

@media (max-width: 600px) {
  ::v-deep .meteo-swatches {
    display: none;
  }

  ::v-deep .meteo-gradiente {
    display: block;
  }

  ::v-deep .meteo-leyenda {
    padding: 6px 8px;
    font-size: 10px;
  }

  ::v-deep .meteo-leyenda-titulo {
    margin-bottom: 4px;
  }
}

::v-deep .activo-market-glow {
  animation: markerGlow 2s infinite ease-in-out;
}

::v-deep .activo-owned-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  color: white;
  font-weight: bold;
  font-size: 11px;
  border: 1.5px solid white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
}

::v-deep .activo-owned-badge.operativo {
  background-color: #2e7d32;
}

::v-deep .activo-owned-badge.no-operativo {
  background-color: #f57f17;
}


</style>